import { createContext, useContext, useState, useCallback, useRef } from 'react';

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When set, a third button is shown between cancel and confirm. */
  secondaryLabel?: string;
  variant?: 'default' | 'danger';
}

/** Return type: true = confirm clicked, 'secondary' = secondary clicked, false = cancel/dismiss */
type ConfirmResult = boolean | 'secondary';

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<ConfirmResult>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: ConfirmResult) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions | string): Promise<ConfirmResult> => {
    const opts = typeof options === 'string' ? { title: options } : options;
    return new Promise<ConfirmResult>((resolve) => {
      resolveRef.current = resolve;
      setDialog(opts);
    });
  }, []);

  function handleClose(result: ConfirmResult) {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setDialog(null);
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {dialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleClose(false);
          }}
        >
          <div className="bg-bg-surface border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-sm font-semibold text-text-primary mb-1">
                {dialog.title}
              </h3>
              {dialog.message && (
                <p className="text-xs text-text-secondary leading-relaxed">
                  {dialog.message}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 pb-4">
              <button
                onClick={() => handleClose(false)}
                className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors"
                autoFocus={dialog.variant === 'danger'}
              >
                {dialog.cancelLabel ?? 'Cancel'}
              </button>
              {dialog.secondaryLabel && (
                <button
                  onClick={() => handleClose('secondary')}
                  className="px-3 py-1.5 text-xs font-medium text-text-secondary border border-border hover:bg-bg-hover rounded-md transition-colors"
                >
                  {dialog.secondaryLabel}
                </button>
              )}
              <button
                onClick={() => handleClose(true)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  dialog.variant === 'danger'
                    ? 'bg-danger text-white hover:bg-danger/90'
                    : 'bg-accent text-white hover:bg-accent-hover'
                }`}
                autoFocus={dialog.variant !== 'danger'}
              >
                {dialog.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
